import { FormEvent, useEffect, useState } from "react";
import useStatusLogList from "./hooks/useStatusLogList";
import "./css/statusLogList.scss";

const getVisiblePages = (currentPage: number, totalPages: number) => {
  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
};

const StatusLogList = () => {
  const hook = useStatusLogList();
  const [macInput, setMacInput] = useState("");
  const { closeDetailModal, selectedDevice } = hook;
  const visiblePages = getVisiblePages(hook.page, hook.totalPages);
  const visibleDetailPages = getVisiblePages(hook.detailPage, hook.detailTotalPages);

  useEffect(() => {
    if (!selectedDevice) return;
    const originalOverflow = document.body.style.overflow;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDetailModal();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [closeDetailModal, selectedDevice]);

  const searchMac = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    hook.setMacSearch(macInput);
  };

  const clearMacSearch = () => {
    setMacInput("");
    hook.setMacSearch("");
  };

  return (
    <section id="statusLogList">
      <header className="status-log__title">
        <div>
          <span>SEOUL BUS · DEVICE LOG</span>
          <h1>서울버스로그</h1>
          <p>서울버스 디바이스의 최근 상태와 수집 로그를 확인합니다.</p>
        </div>
        <button type="button" className="status-log__excel" onClick={hook.downloadExcel}>
          엑셀 다운로드 <span aria-hidden="true">↓</span>
        </button>
      </header>

      <div className="status-log__toolbar" aria-label="로그 조회 조건">
        <form className="status-log__mac-search" onSubmit={searchMac}>
          <span>MAC 주소 검색</span>
          <div>
            <input
              type="search"
              value={macInput}
              onChange={(event) => setMacInput(event.target.value)}
              placeholder="예: 68EDA485D78A"
              aria-label="MAC 주소"
              autoComplete="off"
              spellCheck={false}
            />
            {hook.macSearch && (
              <button type="button" className="is-reset" onClick={clearMacSearch} aria-label="MAC 검색 초기화">×</button>
            )}
            <button type="submit">검색</button>
          </div>
        </form>
        <label>
          <span>기간</span>
          <select value={hook.date} onChange={(event) => hook.setDate(Number(event.target.value))}>
            <option value="1">오늘</option>
            <option value="7">일주일</option>
            <option value="0">전체</option>
          </select>
        </label>
        <label>
          <span>부팅 기준</span>
          <select value={hook.type2} onChange={(event) => hook.setType2(event.target.value)}>
            <option value="1">부팅 전체</option>
            <option value="0">최초 설치</option>
          </select>
        </label>
        <label>
          <span>실행 환경</span>
          <select value={hook.type1} onChange={(event) => hook.setType1(event.target.value)}>
            <option value="main">메인</option>
            <option value="test">메인 + 테스트</option>
          </select>
        </label>
        <label>
          <span>업데이터 버전</span>
          <select value={hook.updaterVersion} onChange={(event) => hook.setUpdaterVersion(event.target.value)}>
            <option value="all">전체</option>
            {hook.updaterVersions.map((version) => (
              <option value={version} key={version}>{version}</option>
            ))}
            <option value="unknown">unknown</option>
          </select>
        </label>
        <label>
          <span>메인앱 버전</span>
          <select value={hook.mainAppVersion} onChange={(event) => hook.setMainAppVersion(event.target.value)}>
            <option value="all">전체</option>
            {hook.mainAppVersions.map((version) => (
              <option value={version} key={version}>{version}</option>
            ))}
            <option value="unknown">unknown</option>
          </select>
        </label>
      </div>

      <div className="status-log__summary">
        <button
          type="button"
          className={hook.statusFilter === "all" ? "is-active" : ""}
          aria-pressed={hook.statusFilter === "all"}
          onClick={() => hook.setStatusFilter("all")}
        >
          <span>조회 디바이스</span>
          <strong>{hook.summaryTotalCount.toLocaleString()}<small>대</small></strong>
        </button>
        <button
          type="button"
          className={`is-normal ${hook.statusFilter === "normal" ? "is-active" : ""}`}
          aria-pressed={hook.statusFilter === "normal"}
          onClick={() => hook.setStatusFilter("normal")}
        >
          <span>정상</span>
          <strong>{hook.normalCount.toLocaleString()}<small>대</small></strong>
        </button>
        <button
          type="button"
          className={`is-warning ${hook.statusFilter === "warning" ? "is-active" : ""}`}
          aria-pressed={hook.statusFilter === "warning"}
          onClick={() => hook.setStatusFilter("warning")}
        >
          <span>점검 필요</span>
          <strong>{hook.warningCount.toLocaleString()}<small>대</small></strong>
        </button>
      </div>

      <div className="status-log__list-header">
        <div>
          <strong>디바이스 목록</strong>
          <span>디바이스를 클릭하면 전체 로그를 확인할 수 있습니다.</span>
        </div>
        {hook.totalCount > 0 && (
          <span>
            {(hook.page - 1) * hook.pageSize + 1}–{Math.min(hook.page * hook.pageSize, hook.totalCount)} / {hook.totalCount}
          </span>
        )}
      </div>

      <div className="status-log__device-list" aria-live="polite">
        {hook.isLoading ? (
          <div className="status-log__state"><span className="status-log__loader" />로그를 불러오는 중입니다.</div>
        ) : hook.errorMessage ? (
          <div className="status-log__state is-error">{hook.errorMessage}</div>
        ) : hook.paginatedList.length === 0 ? (
          <div className="status-log__state">조회 조건에 해당하는 디바이스가 없습니다.</div>
        ) : (
          hook.paginatedList.map((item, index) => (
            <button
              type="button"
              className="status-device"
              key={`${item.id}-${item.mac}`}
              onClick={() => hook.openDetailModal(item)}
            >
              <span className="status-device__number">
                {String((hook.page - 1) * hook.pageSize + index + 1).padStart(2, "0")}
              </span>
              <span className="status-device__identity">
                <strong>{item.mac}</strong>
                <small>ID · {item.id}</small>
              </span>
              <span
                className="status-device__status"
                title={item.check === "1" && item.error_message ? item.error_message : item.status}
              >
                {item.check === "1" && item.error_message ? item.error_message : item.status}
              </span>
              <span className="status-device__versions">
                <small className={item.updater_version ? "" : "is-unknown"}>
                  <b>UPD</b>{item.updater_version || "unknown"}
                </small>
                <small className={item.main_app_version ? "" : "is-unknown"}>
                  <b>APP</b>{item.main_app_version || "unknown"}
                </small>
              </span>
              <span className={`status-device__health ${item.check === "1" ? "is-warning" : "is-normal"}`}>
                <i />{item.check === "1" ? "점검 필요" : "정상"}
              </span>
              <span className="status-device__time" title={hook.dateFormatSecond(item.create_date)}>
                {hook.dateFormat(item.create_date)}
              </span>
              <span className="status-device__open" aria-hidden="true">↗</span>
            </button>
          ))
        )}
      </div>

      {!hook.isLoading && !hook.errorMessage && hook.totalCount > 0 && (
        <nav className="status-pagination" aria-label="디바이스 목록 페이지">
          <button type="button" onClick={() => hook.setPage(1)} disabled={hook.page === 1} aria-label="첫 페이지">«</button>
          <button type="button" onClick={() => hook.setPage(hook.page - 1)} disabled={hook.page === 1} aria-label="이전 페이지">‹</button>
          {visiblePages.map((page) => (
            <button
              type="button"
              className={page === hook.page ? "is-active" : ""}
              aria-current={page === hook.page ? "page" : undefined}
              key={page}
              onClick={() => hook.setPage(page)}
            >
              {page}
            </button>
          ))}
          <button type="button" onClick={() => hook.setPage(hook.page + 1)} disabled={hook.page === hook.totalPages} aria-label="다음 페이지">›</button>
          <button type="button" onClick={() => hook.setPage(hook.totalPages)} disabled={hook.page === hook.totalPages} aria-label="마지막 페이지">»</button>
        </nav>
      )}

      {hook.selectedDevice && (
        <div className="status-modal" role="presentation" onMouseDown={hook.closeDetailModal}>
          <section
            className="status-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="status-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="status-modal__header">
              <div>
                <span>DEVICE LOG</span>
                <h2 id="status-modal-title">디바이스 로그</h2>
                <p>{hook.selectedDevice.mac} · {hook.selectedDevice.id}</p>
              </div>
              <button type="button" onClick={hook.closeDetailModal} aria-label="모달 닫기">×</button>
            </header>

            <div className="status-modal__device-summary">
              <div>
                <span>최근 상태</span>
                <strong title={hook.selectedDevice.status}>{hook.selectedDevice.status}</strong>
              </div>
              <div>
                <span>최근 수신</span>
                <strong>{hook.dateFormatSecond(hook.selectedDevice.create_date)}</strong>
              </div>
              <div>
                <span>업데이터 버전</span>
                <strong>{hook.selectedDevice.updater_version || "unknown"}</strong>
              </div>
              <div>
                <span>메인앱 버전</span>
                <strong>{hook.selectedDevice.main_app_version || "unknown"}</strong>
              </div>
              <div className={hook.selectedDevice.check === "1" ? "is-warning" : "is-normal"}>
                <span>상태 판정</span>
                <strong>{hook.selectedDevice.check === "1" ? "점검 필요" : "정상"}</strong>
              </div>
            </div>

            {hook.selectedDevice.error_message && hook.selectedDevice.error_occurred_at && (
              <div className="status-modal__last-error">
                <span>최근 오류 · {hook.dateFormatSecond(hook.selectedDevice.error_occurred_at)}</span>
                <strong>{hook.selectedDevice.error_message}</strong>
              </div>
            )}

            <div className="status-modal__table-wrap">
              <table className="status-modal__table">
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>수신 시각</th>
                    <th>환경</th>
                    <th>로그 상태</th>
                  </tr>
                </thead>
                <tbody>
                  {hook.isDetailLoading ? (
                    <tr><td colSpan={4}><div className="status-modal__state"><span className="status-log__loader" />상세 로그를 불러오는 중입니다.</div></td></tr>
                  ) : hook.detailErrorMessage ? (
                    <tr><td colSpan={4}><div className="status-modal__state is-error">{hook.detailErrorMessage}</div></td></tr>
                  ) : hook.paginatedDetailList.length === 0 ? (
                    <tr><td colSpan={4}><div className="status-modal__state">수집된 상세 로그가 없습니다.</div></td></tr>
                  ) : (
                    hook.paginatedDetailList.map((detail, index) => (
                      <tr key={`${detail.statusLogId || "log"}-${detail.createDate}-${index}`}>
                        <td>{detail.statusLogId ? `#${detail.statusLogId}` : (hook.detailPage - 1) * hook.detailPageSize + index + 1}</td>
                        <td>{hook.dateFormatSecond(detail.createDate)}</td>
                        <td><span className={`status-env ${detail.test ? "is-test" : "is-main"}`}>{detail.test ? "TEST" : "MAIN"}</span></td>
                        <td>{detail.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {!hook.isDetailLoading && !hook.detailErrorMessage && hook.detailTotalCount > 0 && (
              <footer className="status-modal__footer">
                <span>총 {hook.detailTotalCount.toLocaleString()}건</span>
                <nav className="status-pagination is-compact" aria-label="상세 로그 페이지">
                  <button type="button" onClick={() => hook.setDetailPage(hook.detailPage - 1)} disabled={hook.detailPage === 1} aria-label="이전 상세 페이지">‹</button>
                  {visibleDetailPages.map((page) => (
                    <button
                      type="button"
                      className={page === hook.detailPage ? "is-active" : ""}
                      aria-current={page === hook.detailPage ? "page" : undefined}
                      key={page}
                      onClick={() => hook.setDetailPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button type="button" onClick={() => hook.setDetailPage(hook.detailPage + 1)} disabled={hook.detailPage === hook.detailTotalPages} aria-label="다음 상세 페이지">›</button>
                </nav>
              </footer>
            )}
          </section>
        </div>
      )}
    </section>
  );
};

export default StatusLogList;
